import { CHATYME_WINDOW_EVENT } from "../constants/chatymeWindowEvents";
import { SocketChatSession } from "../types/chat";

export interface ChatymeAgentConfig {
  language?: string;
  notification?: boolean;
  langCodes?: string[];
  defaultLocale?: string;
}

export interface ChatymeConfig {
  openAiDashboard?: {
    project?: string;
    promptEvalProject?: string;
  };
  dailyChatDashboard?: {
    dismissedInfoBanners?: string[];
  };
  agent?: ChatymeAgentConfig;
}

export class StorageHelper {
  static readonly STORAGE_KEYS = {
    AUTH: 'auth',
    USER_TOKEN: 'userToken',
    USER_ID: 'userId',
    USER_EMAIL: 'userEmail',
    USER_ROLE: 'userRole',
    CHATYME_CONFIG: 'chatymeConfig'
  };

  static async getItem(key: string): Promise<string | null> {
    return localStorage.getItem(key);
  }

  static async setItem(key: string, value: string): Promise<void> {
    localStorage.setItem(key, value);
  }

  static async removeItem(key: string): Promise<void> {
    localStorage.removeItem(key);
  }

  static async multiRemove(keys: string[]): Promise<void> {
    keys.forEach(key => localStorage.removeItem(key));
  }

  static async clear(): Promise<void> {
    localStorage.clear();
  }

  public static setCurrentSession = async (session: SocketChatSession | null) => {
    try {
      await this.setItem('chatymeCurrentSession', JSON.stringify(session));
    } catch (error) {
      console.error('Error setting current session:', error);
    }
  };

  public static getCurrentSession = async (): Promise<SocketChatSession | null> => {
    const returnValue = await this.getItem('chatymeCurrentSession');
    return returnValue ? JSON.parse(returnValue) : null;
  };

  private static getChatymeConfigSyncInternal(): ChatymeConfig {
    try {
      const rawConfig = localStorage.getItem(StorageHelper.STORAGE_KEYS.CHATYME_CONFIG);
      if (!rawConfig) {
        return {};
      }

      const parsed = JSON.parse(rawConfig);
      if (!parsed || typeof parsed !== 'object') {
        return {};
      }

      return parsed as ChatymeConfig;
    } catch (error) {
      console.error('Error getting chatyme config:', error);
      return {};
    }
  }

  private static setChatymeConfigSyncInternal(config: ChatymeConfig): void {
    try {
      localStorage.setItem(StorageHelper.STORAGE_KEYS.CHATYME_CONFIG, JSON.stringify(config));
    } catch (error) {
      console.error('Error setting chatyme config:', error);
    }
  }

  static getChatymeConfigSync(): ChatymeConfig {
    return StorageHelper.getChatymeConfigSyncInternal();
  }

  static setChatymeConfigSync(config: ChatymeConfig): void {
    StorageHelper.setChatymeConfigSyncInternal(config);
  }

  static getAgentConfigSync(): ChatymeAgentConfig {
    return StorageHelper.getChatymeConfigSync().agent ?? {};
  }

  static setAgentConfigSync(partial: Partial<ChatymeAgentConfig>): void {
    const currentConfig = StorageHelper.getChatymeConfigSync();
    StorageHelper.setChatymeConfigSync({
      ...currentConfig,
      agent: {
        ...(currentConfig.agent ?? {}),
        ...partial
      }
    });
  }

  static dispatchAgentConfigUpdated(): void {
    window.dispatchEvent(new CustomEvent(CHATYME_WINDOW_EVENT.AGENT_CONFIG_UPDATED));
  }

  public static getChatymeConfig = async (): Promise<ChatymeConfig> => {
    return StorageHelper.getChatymeConfigSync();
  };

  public static setChatymeConfig = async (config: ChatymeConfig): Promise<void> => {
    StorageHelper.setChatymeConfigSync(config);
  };

  public static setOpenAiDashboardProject = async (project: string): Promise<void> => {
    const currentConfig = await StorageHelper.getChatymeConfig();
    await StorageHelper.setChatymeConfig({
      ...currentConfig,
      openAiDashboard: {
        ...(currentConfig.openAiDashboard ?? {}),
        project
      }
    });
  };

  public static getOpenAiDashboardProject = async (): Promise<string> => {
    const currentConfig = await StorageHelper.getChatymeConfig();
    return currentConfig.openAiDashboard?.project ?? '';
  };

  public static setOpenAiDashboardPromptEvalProject = async (project: string): Promise<void> => {
    const currentConfig = await StorageHelper.getChatymeConfig();
    await StorageHelper.setChatymeConfig({
      ...currentConfig,
      openAiDashboard: {
        ...(currentConfig.openAiDashboard ?? {}),
        promptEvalProject: project
      }
    });
  };

  public static getOpenAiDashboardPromptEvalProject = async (): Promise<string> => {
    const currentConfig = await StorageHelper.getChatymeConfig();
    return currentConfig.openAiDashboard?.promptEvalProject ?? '';
  };

  public static getDailyChatDashboardDismissedInfoBannersSync = (): string[] => {
    const banners = StorageHelper.getChatymeConfigSync().dailyChatDashboard?.dismissedInfoBanners;
    return Array.isArray(banners) ? banners.filter((id): id is string => typeof id === 'string') : [];
  };

  public static dismissDailyChatDashboardInfoBannerSync = (bannerId: string): void => {
    const currentConfig = StorageHelper.getChatymeConfigSync();
    const dismissed = StorageHelper.getDailyChatDashboardDismissedInfoBannersSync();
    if (dismissed.includes(bannerId)) {
      return;
    }

    StorageHelper.setChatymeConfigSync({
      ...currentConfig,
      dailyChatDashboard: {
        ...(currentConfig.dailyChatDashboard ?? {}),
        dismissedInfoBanners: [...dismissed, bannerId],
      },
    });
  };
}
